import React, { useState, useEffect, useRef } from 'react';
import './CommandPalette.css';
import { 
    FaSearch, 
    FaTimes, 
    FaTerminal, 
    FaCode, 
    FaEnvelope, 
    FaPhoneAlt, 
    FaGithub, 
    FaLinkedin, 
    FaFileAlt, 
    FaExternalLinkAlt, 
    FaFolderOpen, 
    FaBriefcase, 
    FaGraduationCap, 
    FaLayerGroup
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { trackEvent } from '../utils/analytics';

const CommandPalette = ({ isOpen, onClose, onOpenResume }) => {
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [copiedMsg, setCopiedMsg] = useState('');
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const isUsingKeyboardRef = useRef(false);
    const lastMousePosRef = useRef({ x: -1, y: -1 });

    const commands = [
        // Priority 1: Instant Quick Actions (Top of List for 0.1s Access)
        { 
            id: 'act-resume', 
            title: 'Open Executive CV / Resume (Spec Sheet)', 
            category: 'Quick Action', 
            keywords: 'resume cv spec pdf print bio', 
            icon: <FaFileAlt />, 
            action: () => { onClose(); onOpenResume(); } 
        },
        { 
            id: 'act-email', 
            title: 'Copy Email: devamitin@gmail.com', 
            category: 'Quick Action', 
            keywords: 'email mail copy contact', 
            icon: <FaEnvelope />, 
            action: () => copyText('devamitin@gmail.com', 'Email copied!') 
        },
        { 
            id: 'act-phone', 
            title: 'Copy Mobile: +91 9852972838', 
            category: 'Quick Action', 
            keywords: 'phone mobile tel call number', 
            icon: <FaPhoneAlt />, 
            action: () => copyText('+919852972838', 'Phone copied!') 
        },

        // Priority 2: Verified Production Systems & Deep Jumps
        { 
            id: 'sys-grihseva', 
            title: 'System: Grihseva (Home Services Platform)', 
            category: 'Production System', 
            keywords: 'grihseva nextjs react rsc mongodb', 
            icon: <FaFolderOpen />, 
            action: () => scrollToSection('projects') 
        },
        { 
            id: 'sys-phnk', 
            title: 'System: Phnk Networks (AI Social & Media Platform)', 
            category: 'Production System', 
            keywords: 'phnk ai redis aws microservices webrtc', 
            icon: <FaCode />, 
            action: () => scrollToSection('projects') 
        },
        { 
            id: 'sys-amritpex', 
            title: 'System: AMRITPEX 2023 (National GovTech Infrastructure)', 
            category: 'Production System', 
            keywords: 'amritpex govtech postal dau qr mobile', 
            icon: <FaFolderOpen />, 
            action: () => scrollToSection('projects') 
        },
        { 
            id: 'sys-kalpkriti', 
            title: 'System: Kalpkriti (Workflow Automation & Analytics)', 
            category: 'Production System', 
            keywords: 'kalpkriti bigquery python mongo', 
            icon: <FaCode />, 
            action: () => scrollToSection('projects') 
        },

        // Priority 3: Canonical Section Navigation
        { 
            id: 'nav-home', 
            title: 'Navigate: Home / Identity', 
            category: 'Navigation', 
            keywords: 'home hero start top identity amit', 
            icon: <FaTerminal />, 
            action: () => scrollToSection('hero') 
        },
        { 
            id: 'nav-about', 
            title: 'Navigate: About & Engineering Impact', 
            category: 'Navigation', 
            keywords: 'about bio summary experience philosophy', 
            icon: <FaLayerGroup />, 
            action: () => scrollToSection('about') 
        },
        { 
            id: 'nav-projects', 
            title: 'Navigate: Verified Production Systems', 
            category: 'Navigation', 
            keywords: 'projects portfolio systems works apps', 
            icon: <FaFolderOpen />, 
            action: () => scrollToSection('projects') 
        },
        { 
            id: 'nav-experience', 
            title: 'Navigate: Professional Experience', 
            category: 'Navigation', 
            keywords: 'experience history employment jobs roles', 
            icon: <FaBriefcase />, 
            action: () => scrollToSection('experience') 
        },
        { 
            id: 'nav-skills', 
            title: 'Navigate: Core Engineering Competencies (Skills)', 
            category: 'Navigation', 
            keywords: 'skills frontend backend cloud node react docker', 
            icon: <FaCode />, 
            action: () => scrollToSection('skills') 
        },
        { 
            id: 'nav-credentials', 
            title: 'Navigate: Academic Foundations & Credentials', 
            category: 'Navigation', 
            keywords: 'education degree btech credentials', 
            icon: <FaGraduationCap />, 
            action: () => scrollToSection('credentials') 
        },
        { 
            id: 'nav-contact', 
            title: 'Navigate: Contact & Inquiries', 
            category: 'Navigation', 
            keywords: 'contact message transmission email', 
            icon: <FaEnvelope />, 
            action: () => scrollToSection('contact') 
        },

        // Priority 4: Verified External Networks
        { 
            id: 'ext-github', 
            title: 'Visit GitHub Profile (@devamitin)', 
            category: 'External Links', 
            keywords: 'github repo code open source git', 
            icon: <FaGithub />, 
            action: () => openExternal('https://github.com/devamitin') 
        },
        { 
            id: 'ext-linkedin', 
            title: 'Visit LinkedIn Profile (/in/devamitin)', 
            category: 'External Links', 
            keywords: 'linkedin connect network profile', 
            icon: <FaLinkedin />, 
            action: () => openExternal('https://linkedin.com/in/devamitin') 
        },
        { 
            id: 'ext-npm', 
            title: 'Browse Published NPM Suite (~devamitin)', 
            category: 'External Links', 
            keywords: 'npm packages smart-env-switcher library', 
            icon: <FaExternalLinkAlt />, 
            action: () => openExternal('https://www.npmjs.com/~devamitin') 
        },
        { 
            id: 'ext-x', 
            title: 'Visit X / Twitter Profile (@devamitin)', 
            category: 'External Links', 
            keywords: 'x twitter social tweet profile', 
            icon: <FaXTwitter />, 
            action: () => openExternal('https://x.com/devamitin') 
        },
    ];

    const scrollToSection = (id) => {
        onClose();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    const copyText = (text, feedback) => {
        navigator.clipboard.writeText(text);
        setCopiedMsg(feedback);
        setTimeout(() => {
            setCopiedMsg('');
            onClose();
        }, 1200);
    };

    const openExternal = (url) => {
        onClose();
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const filtered = commands.filter(c =>
        c.title.toLowerCase().includes(query.toLowerCase()) ||
        c.category.toLowerCase().includes(query.toLowerCase()) ||
        (c.keywords && c.keywords.toLowerCase().includes(query.toLowerCase()))
    );

    const handleItemMouseMove = (idx, e) => {
        if (
            lastMousePosRef.current.x === e.clientX &&
            lastMousePosRef.current.y === e.clientY
        ) {
            return;
        }

        const hasMoved =
            Boolean(e.movementX || e.movementY) ||
            (lastMousePosRef.current.x !== -1 &&
                (lastMousePosRef.current.x !== e.clientX || lastMousePosRef.current.y !== e.clientY));

        lastMousePosRef.current = { x: e.clientX, y: e.clientY };

        if (!hasMoved) {
            return;
        }

        isUsingKeyboardRef.current = false;
        setSelectedIndex(idx);
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setQuery('');
            setSelectedIndex(0);
            lastMousePosRef.current = { x: -1, y: -1 };
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        setSelectedIndex(0);
        lastMousePosRef.current = { x: -1, y: -1 };
        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
    }, [query]);

    useEffect(() => {
        if (listRef.current) {
            const selectedEl = listRef.current.querySelector('.palette-item.selected');
            if (selectedEl) {
                selectedEl.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
            }
        }
    }, [selectedIndex]);

    const executeItem = (item) => {
        if (!item) return;
        trackEvent('command_executed', { command_id: item.id, title: item.title });
        item.action();
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                isUsingKeyboardRef.current = true;
                setSelectedIndex(prev => (prev + 1) % (filtered.length || 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                isUsingKeyboardRef.current = true;
                setSelectedIndex(prev => (prev - 1 + (filtered.length || 1)) % (filtered.length || 1));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (filtered[selectedIndex]) {
                    executeItem(filtered[selectedIndex]);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, filtered, selectedIndex, onClose]);

    if (!isOpen) return null;

    return (
        <div className="palette-backdrop" onClick={onClose}>
            <div className="palette-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Command Palette">
                <div className="palette-header">
                    <FaSearch className="palette-search-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="palette-input"
                        placeholder="Type a command or search... (e.g. Projects, Resume)"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />
                    {query && (
                        <button className="palette-clear-btn" onClick={() => setQuery('')} aria-label="Clear query">
                            <FaTimes />
                        </button>
                    )}
                    <button className="palette-close-btn" onClick={onClose} aria-label="Close command palette">
                        <FaTimes />
                    </button>
                </div>

                {copiedMsg && (
                    <div className="palette-copied-banner">
                        <span>{copiedMsg}</span>
                    </div>
                )}

                <div 
                    className="palette-results" 
                    ref={listRef}
                >
                    {filtered.length === 0 ? (
                        <div className="palette-empty">
                            <FaTerminal className="empty-icon" />
                            <p>No matching commands found for "{query}"</p>
                        </div>
                    ) : (
                        filtered.map((item, idx) => (
                            <div
                                key={item.id}
                                className={`palette-item ${idx === selectedIndex ? 'selected' : ''}`}
                                onClick={() => executeItem(item)}
                                onMouseMove={(e) => handleItemMouseMove(idx, e)}
                            >
                                <span className="palette-item-icon">{item.icon}</span>
                                <span className="palette-item-title">{item.title}</span>
                                <span className={`palette-item-category ${item.category.toLowerCase().replace(/\s+/g, '-')}`}>{item.category}</span>
                            </div>
                        ))
                    )}
                </div>

                <div className="palette-footer">
                    <span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
                    <span><kbd>↵</kbd> Execute</span>
                    <span><kbd>ESC</kbd> Close</span>
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
